import {Component, EventEmitter, Input, OnInit, Output, OnDestroy, ViewChild, ElementRef} from "@angular/core";
import {ConfigStateService} from "../../../../app/config/config-state.service";
import { ActivatedRoute } from "@angular/router";
import { Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ProcessStatePollingService } from "../../../services/process-state-polling.service";
import { Processes, ProcessStates } from "../../../types/processState";
import { BulkUpdateTaxReturnService } from "../../../services/bulk-update-tax-return.service";
import { CurrentBatchService } from "../../../services/current-batch.service";
import { Tenant } from "../../../types/tenant.enum";
import { TenantConfigService } from "../../../services/tenant-config.service";
import { TranslocoService} from "@jsverse/transloco";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { BulkStatusUpdateComponent } from "./bulk-status-update/bulk-status-update.component";
import { BulkMoveAssigneesComponent } from "./bulk-move-assignees/bulk-move-assignees.component";
import { CreateBatch } from "./create-batch/create-batch.component";
import { AddToBatch } from "./add-to-batch/add-to-batch.component";
import { RemoveFromBatch } from "./remove-from-batch/remove-from-batch.component";
import { BulkGeneratePackage } from "./bulk-generate-package/bulk-generate-package.component";
import { BatchGeneratePackage } from "./batch-generate-package/batch-generate-package.component";
import { BatchSendPackage } from "./batch-send-package/batch-send-package.component";
import { SchedulePackageGeneration } from "./schedule-package-generation/schedule-package-generation.component";

@Component({
    selector: "app-tax-return-bulk-actions",
    templateUrl: "./tax-return-bulk-actions.component.html",
    styleUrls: ["./tax-return-bulk-actions.component.scss"],
    standalone: false
})
export class TaxReturnBulkActionsComponent implements OnInit, OnDestroy {
  @Input() selectedTaxReturns;
  @Input() year;
  @Input() currentBatchList;
  @Input() customFilters;
  @Input() selectedBatchId;
  @Input() taxReturnTypeById
  @Input() taxReturnSubTypeById
  @Input() client
  @Input() taxReturnAlias: string;
  @Input() taxpayerAlias: string;
  @Output() selectAllChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() completed = new EventEmitter();
  @ViewChild('selectAllCheckBox') selectAllRef: ElementRef;

  currentBatch = null;
  actionList = [];
  selectedActionId = "";
  selectAll: boolean = false;
  clientId = "";
  populationId = "";
  errorMessage = "";
  isPackageGenerating = false;
  labelGenerateDraft: boolean = true;
  subscriptions: Subscription[] = [];
  private activeDialogRef?: MatDialogRef<unknown>;

  private unsubscribe: Subject<void> = new Subject();
  constructor(private config: ConfigStateService,
    private route: ActivatedRoute,
    private processStatePollingService: ProcessStatePollingService,
    private bulkUpdateTaxReturnService: BulkUpdateTaxReturnService,
    private currentBatchService: CurrentBatchService,
    private translocoService: TranslocoService,
    private dialog: MatDialog
    ) {
  }

  get selectedTaxReturnIds() {
    return this.selectedTaxReturns ?  Object.keys(this.selectedTaxReturns).map(n=>Number(n)) : [];
  }

  ngOnInit() {
    this.translocoService.selectTranslate('tax-return-alias').subscribe(value => this.taxReturnAlias = value);
    this.currentBatchService.currentBatch.subscribe( result => {
      this.currentBatch = result;
    });
    this.actionList = this.getActionList().filter(action => action.enabled);
    this.route.params
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(params => {
      this.clientId = params.clientId || "";
      this.populationId = params.populationId || "";
    });
    this.checkPackageGenerationState();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  updateSelectAll(selectAll) {
    this.revalidate();
    this.selectAllChanged.emit(selectAll);
  }

  selectAction(actionId) {
    const disableState = this.isDisabled(actionId);
    if (disableState.disabled) {
      this.errorMessage = disableState.message;
    } else {
      this.errorMessage = '';
      if(actionId === 'ACTION_UPDATE'){
        this.goToBulkActions();
      } else {
        this.openActionDialog(actionId);
      }
    }
  }

  actionCompleted(actionId) {
    this.selectedActionId = "";
    this.selectAllRef.nativeElement.checked = false;
    this.selectAll = false;
    this.revalidate();
    this.selectAllChanged.emit(false);
    this.completed.emit();
  }

  goToBulkActions(){
    this.bulkUpdateTaxReturnService.setTaxReturnList(Object.values(this.selectedTaxReturns));
    this.bulkUpdateTaxReturnService.setActionViewFlag(true);
  }

  private revalidate() {
    const disableState = this.isDisabled(this.selectedActionId);
    if (!disableState.disabled) {
      this.errorMessage = '';
      this.selectedActionId = '';
    }
  }

  private isDisabled(actionId) {
    if (!actionId) return {disabled: false, message: ''};
    if (actionId === 'GENERATE_PACKAGE' || actionId === 'GENERATE_BATCH_PACKAGE' || actionId === 'GENERATE_BATCH_DRAFT') {
      if (this.selectedTaxReturnIds.length === 0) {
        return {
          disabled: true,
          message: `Select at least 1 ${this.taxReturnAlias}`
        }
      }
      if (actionId === 'GENERATE_BATCH_PACKAGE' || actionId === 'GENERATE_BATCH_DRAFT') {
        if (!this.currentBatch) {
          return {
            disabled: true,
            message: 'This action can only be applied to a batch. Select a batch and try again.'
          };
        }
      }
      return  {
        disabled: this.isPackageGenerating,
        message: this.isPackageGenerating ? 'Package generation is progress, please try again later' : ''
      };
    } else if (actionId === 'SEND_BATCH_PACKAGE') {
      if (!this.currentBatch) {
        return {
          disabled: true,
          message: 'This action can only be applied to a batch. Select a batch and try again.'
        };
      }
      const disabled = this.selectedTaxReturnIds.length !== this.currentBatch?.taxReturnIds?.length;
      return {
        disabled,
        message: disabled ? `Must select all  ${this.taxReturnAlias}` : ''
      };
    } else {
      return  {
        disabled: this.selectedTaxReturnIds.length <= 0,
        message: this.selectedTaxReturnIds.length  <= 0 ? `Select at least 1  ${this.taxReturnAlias}` : ''
      };
    }
  }

  private checkPackageGenerationState() {
    this.processStatePollingService
      .subscribeData(Processes.EssGeneration, 'population', this.populationId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        const isGenerating = (response && response.state && response.state === ProcessStates.RUNNING);
        // if generation finished
        if (isGenerating !== this.isPackageGenerating && !isGenerating) {
          this.revalidate();
          this.completed.emit(true);
        }
        this.isPackageGenerating = isGenerating;
      });
  }

  private getGenerateText() {
    if(this.config.tenant === Tenant.CN || this.config.tenant === Tenant.HK){
      return 'preview';
    }
    return 'generate';
  }

  private getActionList() {
    return [
      {id: "STATUS_UPDATE", label: `Update  ${this.taxReturnAlias} status`, enabled: this.config.featureFlags.bulkTaxReturnStatusUpdate},
      {id: "ACTION_UPDATE", label: "Apply action", enabled: this.config.featureFlags.bulkTaxReturnActionUpdate},
      {id: "MOVE_ASSIGNEES", label: "Move assignees to population", enabled: this.config.featureFlags.bulkMoveAssigneeToPopulation},
      {id: "CREATE_BATCH", label: "Create new batch", enabled: this.config.featureFlags.batchGeneratePackage},
      {id: "ADD_TO_BATCH", label: "Add to batch", enabled: this.config.featureFlags.batchGeneratePackage},
      {id: "REMOVE_FROM_BATCH", label: "Remove from batch", enabled: this.config.featureFlags.batchGeneratePackage},
      {id: "GENERATE_BATCH_DRAFT", label: "Schedule " + this.getGenerateText()+" draft package", enabled: this.config.featureFlags.batchGeneratePackage},
      {id: "GENERATE_BATCH_PACKAGE", label: "Finalise package", enabled: this.config.featureFlags.batchGeneratePackage},
      {id: "SEND_BATCH_PACKAGE", label: this.config.tenant === Tenant.FR ? `Send final package to  ${this.taxpayerAlias}` : 'Send final package', enabled: this.config.featureFlags.batchGeneratePackage},
      {id: "GENERATE_PACKAGE", label: "Generate final package", enabled: this.config.featureFlags.bulkGeneratePackage},
      //{id: "SCHEDULE_PACKAGE_GENERATION", label: "Schedule Draft package generation", enabled: this.config.featureFlags.batchGeneratePackage && this.config.featureFlags.queueBatchingForBulkPackageGeneration},
    ];
  }

  private openActionDialog(actionId: string) {
    this.activeDialogRef?.close();

    const dialogRef = this.getDialogRef(actionId);
    if (!dialogRef) {
      return;
    }

    this.activeDialogRef = dialogRef;
    dialogRef.afterClosed().subscribe(() => {
      if (this.activeDialogRef !== dialogRef) {
        return;
      }
      this.activeDialogRef = undefined;
      this.actionCompleted(actionId);
    });
  }

  private getDialogRef(actionId: string): MatDialogRef<unknown> | undefined {
    switch (actionId) {
      case 'STATUS_UPDATE':
        return this.dialog.open(BulkStatusUpdateComponent, {
          data: { selectedTaxReturns: this.selectedTaxReturns, taxReturnAlias: this.taxReturnAlias },
          autoFocus: false,
        });
      case 'MOVE_ASSIGNEES':
        return this.dialog.open(BulkMoveAssigneesComponent, {
          data: {
            selectedTaxReturns: this.selectedTaxReturns,
            taxpayerAlias: this.taxpayerAlias,
            currentClientId: this.clientId,
            currentPopulationId: this.populationId
          },
          autoFocus: false,
        });
      case 'CREATE_BATCH':
        return this.dialog.open(CreateBatch, {
          data: {
            taxReturnAlias: this.taxReturnAlias,
            selectedTaxReturns: this.selectedTaxReturns,
            currentClientId: this.clientId,
            customFilters: this.customFilters,
            year: this.year,
            taxReturnTypeById: this.taxReturnTypeById,
            taxReturnSubTypeById: this.taxReturnSubTypeById,
            client: this.client,
          },
          autoFocus: false,
        });
      case 'ADD_TO_BATCH':
        if (!this.currentBatchList?.length) {
          return null;
        }
        return this.dialog.open(AddToBatch, {
          data: {
            selectedTaxReturns: this.selectedTaxReturns,
            selectedBatchFilter: this.selectedBatchId,
            taxReturnAlias: this.taxReturnAlias,
            currentBatchList: this.currentBatchList
          },
          autoFocus: false,
        });
      case 'REMOVE_FROM_BATCH':
        if (!this.currentBatchList?.length) {
          return null;
        }
        return this.dialog.open(RemoveFromBatch, {
          data: {
            selectedTaxReturns: this.selectedTaxReturns,
            selectedBatchFilter: this.selectedBatchId,
            taxReturnAlias: this.taxReturnAlias,
            currentBatchList: this.currentBatchList
          },
          autoFocus: false,
        });
      case 'GENERATE_PACKAGE':
        return this.dialog.open(BulkGeneratePackage, {
          data: {
            selectedTaxReturns: this.selectedTaxReturns,
            taxReturnAlias: this.taxReturnAlias,
            populationId: this.populationId
          },
          autoFocus: false,
        });
      case 'GENERATE_BATCH_PACKAGE':
        return this.dialog.open(BatchGeneratePackage, {
          data: { selectedTaxReturns: this.selectedTaxReturns,
            taxReturnAlias: this.taxReturnAlias,
            batchId: this.selectedBatchId,
            clientId: this.clientId,
            year: this.year,
            genType: 'STANDARD'
          },
          autoFocus: false,
        });
      case 'GENERATE_BATCH_DRAFT':
        return this.dialog.open(BatchGeneratePackage, {
          data: {
            selectedTaxReturns: this.selectedTaxReturns,
            taxReturnAlias: this.taxReturnAlias,
            batchId: this.selectedBatchId,
            clientId: this.clientId,
            year: this.year, genType: 'DRAFT'
          },
          autoFocus: false,
        });
      case 'SEND_BATCH_PACKAGE':
        return this.dialog.open(BatchSendPackage, {
          data: {
            selectedTaxReturns: this.selectedTaxReturns,
            batchId: this.selectedBatchId,
            clientId: this.clientId,
            year: this.year,
            genType: 'STANDARD',
            taxReturnAlias: this.taxReturnAlias
          },
          autoFocus: false,
        });
      case 'SCHEDULE_PACKAGE_GENERATION':
        return this.dialog.open(SchedulePackageGeneration, {
          data: {
            selectedTaxReturns: this.selectedTaxReturns,
            currentBatchList: this.currentBatchList,
            batchId: this.selectedBatchId,
            clientId: this.clientId,
            year: this.year
          },
          autoFocus: false,
        });
    }
  }
}
